/**
 * What each surface may do about a background-location result.
 *
 * **This file imports nothing**, like its neighbours, and for the reason this
 * change exists: the components that consume these decisions import
 * react-native and can never be loaded by a test, so a decision left inside
 * them can only ever be asserted against its own source text. That is exactly
 * how the defect below shipped.
 *
 * ## The defect
 *
 * `c8e313fe` put a 2 s deadline on the permission reads and returned
 * "unavailable" when one expired. "unavailable" already had a consumer: the
 * prompt sheet's final `else`, whose comment reads *"Not available here (e.g.
 * Expo Go / web)"* — states that cannot occur on a real handset. It persists
 * `while_open_only`.
 *
 * Then `11ddd662` taught `resolve` to stamp `demotedFrom: null`, and taught
 * `reconcileStayVisible` that a null `demotedFrom` means a deliberate choice it
 * must never override.
 *
 * Together, shipped the same day: a two-second OS hiccup during one tap
 * permanently disabled staying visible, and the recovery built alongside it was
 * specifically designed not to undo that state. The meaning of a return value
 * changed and its callers were never re-read.
 *
 * ## Why the plans are discriminated unions
 *
 * An open bag — `{ mode, start, verify, clearRepair }` — permits
 * `{ mode: null, start: true }`: persist nothing, but launch the background
 * task anyway. The whole point of "unknown" is that it mutates NOTHING, and a
 * shape that can express the opposite leaves that to a reviewer's attention.
 * Each variant below fixes every field together, so the impossible combinations
 * cannot be written down.
 *
 * The exhaustive switches live HERE and only here. Repeating a result switch in
 * the components would be a second decision mechanism, free to disagree with
 * this one.
 */

/** Everything a background-location attempt can conclude. */
export type BackgroundLocationResult =
  /** The OS grants background location. */
  | "granted"
  /** A person refused: a prompt was declined, or a completed Settings trip
   *  changed nothing. An answer, and safe to record. */
  | "denied"
  /** Positively identified as unsupported — web. Not a failure to find out. */
  | "unavailable"
  /** We could not find out: a read timed out, a native call threw, Settings
   *  would not open, or we never saw the round trip. NOT a refusal, and the
   *  distinction is the entire subject of this file. */
  | "unknown";

/** Which message to show. The words live with `t`; the choice lives here. */
export type PromptNotice = "backgroundOff" | "unavailable" | "temporaryFailure";
export type RepairNotice = "stillOff" | "temporaryFailure";

/**
 * The "Stay visible?" sheet.
 *
 * `mode` is the value to persist, or null to persist nothing at all.
 */
export type PromptPlan =
  | { kind: "enable"; mode: "enabled"; start: true; notice: null }
  | { kind: "deny"; mode: "denied"; start: false; notice: "backgroundOff" }
  | { kind: "fallback"; mode: "while_open_only"; start: false; notice: "unavailable" }
  | { kind: "retry"; mode: null; start: false; notice: "temporaryFailure" };

/** The quiet repair sheet, which persists nothing except on success. */
export type RepairPlan =
  | {
      kind: "enable";
      mode: "enabled";
      start: true;
      verify: true;
      clearRepair: true;
      notice: null;
    }
  | {
      kind: "stillOff";
      mode: null;
      start: false;
      verify: false;
      clearRepair: false;
      notice: "stillOff";
    }
  | {
      kind: "retry";
      mode: null;
      start: false;
      verify: false;
      clearRepair: false;
      notice: "temporaryFailure";
    };

/** Fails the build if a result is ever added without being handled here. */
function unreachable(value: never): never {
  throw new Error(`unhandled background result: ${String(value)}`);
}

export function promptPlan(result: BackgroundLocationResult): PromptPlan {
  switch (result) {
    case "granted":
      return { kind: "enable", mode: "enabled", start: true, notice: null };
    case "denied":
      // A real answer, so it is recorded — and recoverable: `reconcile` adopts
      // "denied" if the OS later grants, because "denied" describes the OS
      // rather than the person.
      return { kind: "deny", mode: "denied", start: false, notice: "backgroundOff" };
    case "unavailable":
      // Only web produces this — `requestBackgroundLocation` returns it for
      // `Platform.OS === "web"` and for nothing else. Persisting the fallback
      // is correct: the platform genuinely cannot do it and never will.
      //
      // UNREACHABLE FROM THE UI since 2026-08-31, and kept anyway. Web can no
      // longer open the prompt at all (`maybePrompt`/`forcePrompt` refuse, and
      // the Profile row is not rendered), so `onOkay` never runs there. The
      // branch stays because this function is the exhaustive owner of the
      // decision and a missing case would fall through to a prompt; but note
      // that `visibility.notAvailableBody` still blames "the full app build",
      // which was never the real reason on web. If anything ever makes this
      // reachable again, fix that string before shipping it.
      return {
        kind: "fallback",
        mode: "while_open_only",
        start: false,
        notice: "unavailable",
      };
    case "unknown":
      // NOTHING. This is the whole fix. Persisting `while_open_only` here is
      // what disabled the feature permanently, because it is indistinguishable
      // from the user choosing it and `reconcile` refuses to override a choice.
      return { kind: "retry", mode: null, start: false, notice: "temporaryFailure" };
    default:
      return unreachable(result);
  }
}

export function repairPlan(result: BackgroundLocationResult): RepairPlan {
  switch (result) {
    case "granted":
      return {
        kind: "enable",
        mode: "enabled",
        start: true,
        verify: true,
        clearRepair: true,
        notice: null,
      };
    case "denied":
    case "unavailable":
      // This sheet persists nothing on a refusal — the user is already
      // app-open-only by the time it appears, so there is nothing to write.
      return {
        kind: "stillOff",
        mode: null,
        start: false,
        verify: false,
        clearRepair: false,
        notice: "stillOff",
      };
    case "unknown":
      // Same as above in its effects, and deliberately NOT the same message:
      // "background is still off" is a claim we have not established.
      return {
        kind: "retry",
        mode: null,
        start: false,
        verify: false,
        clearRepair: false,
        notice: "temporaryFailure",
      };
    default:
      return unreachable(result);
  }
}
